import { LocalDate } from '@js-joda/core';

import { Postcode } from '@dreryk/address-common-api';
import { LegalBasis, Patient } from '@dreryk/egabinet-patient-frontend-api';

import { commonTranslationKeys, FormContextValues, useFormController } from '@dreryk/react-common';
import { AutoLayout, ModalCancelButton, ModalCloseButton } from '@dreryk/react-components';
import { Range } from '@dreryk/watermelon';

import { AddressSection } from '../../../common/EntitlementStatementForm/sections/AddressSection';
import { DateSection } from '../../../common/EntitlementStatementForm/sections/DateSection';
import { EntitlementStatementSection } from '../../../common/EntitlementStatementForm/sections/EntitlementStatementSection';
import { EntitlementStatementCreateModalProps } from '../modal/EntitlementStatementCreateModal';
import { PatientModalConfirmationButton } from '../modal/PatientModalConfirmationButton';

import { EntitlementStatementCreateFormBean } from './EntitlementStatementCreateForm';
import {
   createEntitlementStatementIdentityDocumentFormPart,
   EntitlementStatementIdentityDocumentFormPart,
   EntitlementStatementIdentityDocumentFormSection,
} from './EntitlementStatementIdentityDocumentFormSection';

export type PatientAddressFormBean = {
   street: string;
   house: string;
   flat: string | null;
   town: string | null;
   postcode: Postcode | null;
};

export type PatientEntitlementStatementCreateFormBean = PatientAddressFormBean &
   EntitlementStatementCreateFormBean & {
      identityDocument: EntitlementStatementIdentityDocumentFormPart;
   };

export type PatientEntitlementStatementCreateFormController = FormContextValues<PatientEntitlementStatementCreateFormBean>;

export const getInitialValues = (
   patient: Patient | null,
   legalBasis: LegalBasis[]
): PatientEntitlementStatementCreateFormBean => {
   const [defaultSelectedLegalBasis] = legalBasis;
   return {
      identityDocument: createEntitlementStatementIdentityDocumentFormPart(patient?.identityDocument),
      flat: patient?.address?.findFlat()?.asString() ?? null,
      house: patient?.address?.findBuilding()?.asString() ?? '',
      street: patient?.address?.findStreet()?.asString() ?? '',
      town: patient?.address?.findTown()?.getName() ?? null,
      postcode: patient?.address?.findPostcode() ?? null,
      legalBasis: defaultSelectedLegalBasis,
      submissionDate: LocalDate.now(),
      effectiveDate: LocalDate.now(),
      grantingDateFieldEnabled: false,
      grantingDate: Range.closed(LocalDate.now(), LocalDate.now()),
      printWithExplanationsAndNotice: false,
   };
};

export type PatientEntitlementStatementCreateFormContentProps = EntitlementStatementCreateModalProps & {
   readonly patient: Patient | null;
   readonly legalBasis: LegalBasis[];
};

export const EntitlementStatementPatientDataFormBlock = (
   props: Pick<FormContextValues<PatientEntitlementStatementCreateFormBean>, 'register' | 'setValue' | 'watch'>
): JSX.Element => {
   const { register, setValue, watch } = props;
   return (
      <>
         <EntitlementStatementIdentityDocumentFormSection
            name={'identityDocument'}
            register={register}
            setValue={setValue}
         />
         <AddressSection register={register} />
         <DateSection register={register} watch={watch} />
      </>
   );
};

export const PatientEntitlementStatementCreateFormContent = (
   props: PatientEntitlementStatementCreateFormContentProps
): JSX.Element => {
   const {
      register,
      formState,
      setValue,
      watch,
      handleSubmit,
      trigger,
   } = useFormController<PatientEntitlementStatementCreateFormBean>(getInitialValues(props.patient, props.legalBasis));

   const { effectiveDate, submissionDate } = watch(['effectiveDate', 'submissionDate']);

   return (
      <>
         <AutoLayout as={'form'} noValidate $direction={'vertical'} $horizontalResizing={750} $spacing={24}>
            <EntitlementStatementSection register={register} legalBasis={props.legalBasis} />
            <EntitlementStatementPatientDataFormBlock setValue={setValue} watch={watch} register={register} />
         </AutoLayout>
         <PatientModalConfirmationButton
            handleSubmit={handleSubmit}
            trigger={trigger}
            patientId={props.patientId}
            differentDates={submissionDate && effectiveDate ? !effectiveDate.isEqual(submissionDate) : true}
            onDataChanged={props.onDataChanged}
            setEntitlementStatementId={props.setEntitlementStatementId}
         />
         <ModalCancelButton
            label={commonTranslationKeys.cancel}
            onClick={props.onCancel}
            shouldConfirmExit={formState.isDirty}
         />
         <ModalCloseButton onClick={props.onCancel} shouldConfirmExit={formState.isDirty} />
      </>
   );
};
